<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import BillingAPI from 'dashboard/api/billing';
import { useBillingCheckout } from 'dashboard/composables/useBillingCheckout';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  planName: { type: String, default: '' },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const { isPaying, addSeats } = useBillingCheckout();

const dialogRef = ref(null);
const agentsQty = ref(0);
const inboxesQty = ref(0);
const preview = ref(null);
const isPreviewLoading = ref(false);
const previewError = ref('');
const agentsUnitPrice = ref(null);
const inboxesUnitPrice = ref(null);
const previewPlanName = ref('');
const isUnitPriceLoading = ref(false);
const currencySymbol = ref('');

const seatOptions = Array.from({ length: 11 }, (_, i) => ({
  value: i,
  label: String(i),
}));

const displayPlanName = computed(
  () => previewPlanName.value || props.planName || ''
);

const hasQuantity = computed(
  () => Number(agentsQty.value) > 0 || Number(inboxesQty.value) > 0
);

const canConfirm = computed(
  () =>
    hasQuantity.value &&
    preview.value &&
    !isPreviewLoading.value &&
    !previewError.value
);

const formatMoney = (amount, symbol) => {
  const formatted = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol || ''}${formatted}`;
};

const periodNote = computed(() => {
  if (!preview.value) return '';
  return t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_PERIOD_NOTE', {
    plan: preview.value.plan_name,
    days: preview.value.period_days,
    remaining: preview.value.remaining_days,
  });
});

const agentsUnitPriceLabel = computed(() => {
  if (agentsUnitPrice.value == null) return '';
  return t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_UNIT_PRICE', {
    price: formatMoney(agentsUnitPrice.value, currencySymbol.value),
  });
});

const inboxesUnitPriceLabel = computed(() => {
  if (inboxesUnitPrice.value == null) return '';
  return t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_UNIT_PRICE', {
    price: formatMoney(inboxesUnitPrice.value, currencySymbol.value),
  });
});

const fetchUnitPrices = async () => {
  isUnitPriceLoading.value = true;
  try {
    const [agentsRes, inboxesRes] = await Promise.all([
      BillingAPI.previewSeatAddition({
        agents_quantity: 1,
        inboxes_quantity: 0,
      }),
      BillingAPI.previewSeatAddition({
        agents_quantity: 0,
        inboxes_quantity: 1,
      }),
    ]);
    const agentsPayload = agentsRes.data.payload;
    const inboxesPayload = inboxesRes.data.payload;
    const agentsLine = agentsPayload?.line_items?.find(
      line => line.limit_key === 'agents'
    );
    const inboxesLine = inboxesPayload?.line_items?.find(
      line => line.limit_key === 'inboxes'
    );
    agentsUnitPrice.value = agentsLine?.unit_price ?? null;
    inboxesUnitPrice.value = inboxesLine?.unit_price ?? null;
    previewPlanName.value =
      agentsPayload?.plan_name || inboxesPayload?.plan_name || '';
    currencySymbol.value =
      agentsPayload?.currency_symbol || inboxesPayload?.currency_symbol || '';
  } catch {
    agentsUnitPrice.value = null;
    inboxesUnitPrice.value = null;
  } finally {
    isUnitPriceLoading.value = false;
  }
};

const fetchPreview = async () => {
  if (!hasQuantity.value) {
    preview.value = null;
    previewError.value = '';
    return;
  }

  isPreviewLoading.value = true;
  previewError.value = '';
  try {
    const { data } = await BillingAPI.previewSeatAddition({
      agents_quantity: Number(agentsQty.value) || 0,
      inboxes_quantity: Number(inboxesQty.value) || 0,
    });
    preview.value = data.payload;
  } catch (error) {
    preview.value = null;
    previewError.value =
      error?.response?.data?.error ||
      t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_PREVIEW_ERROR');
  } finally {
    isPreviewLoading.value = false;
  }
};

const debouncedPreview = useDebounceFn(fetchPreview, 350);

watch([agentsQty, inboxesQty], debouncedPreview);

const openModal = async () => {
  if (props.disabled) return;

  agentsQty.value = 0;
  inboxesQty.value = 0;
  preview.value = null;
  previewError.value = '';
  agentsUnitPrice.value = null;
  inboxesUnitPrice.value = null;
  dialogRef.value?.open();
  await fetchUnitPrices();
};

const onConfirm = async () => {
  if (!canConfirm.value) return;

  const ok = await addSeats({
    agentsQuantity: Number(agentsQty.value) || 0,
    inboxesQuantity: Number(inboxesQty.value) || 0,
  });
  if (ok) {
    dialogRef.value?.close();
    emit('updated');
  }
};

defineExpose({ open: openModal });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_MODAL_TITLE')"
    :description="
      t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_MODAL_DESCRIPTION')
    "
    :confirm-button-label="
      t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_CONFIRM')
    "
    :disable-confirm-button="!canConfirm"
    :is-loading="isPaying"
    width="md"
    @confirm="onConfirm"
  >
    <p v-if="displayPlanName" class="mb-0 text-sm font-medium text-n-slate-12">
      {{
        t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_CURRENT_PLAN', {
          plan: displayPlanName,
        })
      }}
    </p>

    <p v-if="periodNote" class="mb-0 text-sm text-n-slate-11">
      {{ periodNote }}
    </p>

    <div class="grid gap-4 sm:grid-cols-2">
      <div class="flex flex-col gap-1.5">
        <Select
          v-model="agentsQty"
          :options="seatOptions"
          :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_AGENTS_QTY')"
          :disabled="isPaying"
          full-width
        />
        <p v-if="isUnitPriceLoading" class="mb-0 text-xs text-n-slate-11">
          {{ t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.LOADING') }}
        </p>
        <p
          v-else-if="agentsUnitPriceLabel"
          class="mb-0 text-xs text-n-slate-11"
        >
          {{ agentsUnitPriceLabel }}
        </p>
      </div>

      <div class="flex flex-col gap-1.5">
        <Select
          v-model="inboxesQty"
          :options="seatOptions"
          :label="
            t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_INBOXES_QTY')
          "
          :disabled="isPaying"
          full-width
        />
        <p
          v-if="!isUnitPriceLoading && inboxesUnitPriceLabel"
          class="mb-0 text-xs text-n-slate-11"
        >
          {{ inboxesUnitPriceLabel }}
        </p>
      </div>
    </div>

    <p v-if="!hasQuantity" class="mb-0 text-sm text-n-slate-11">
      {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_EMPTY') }}
    </p>

    <p v-else-if="isPreviewLoading" class="mb-0 text-sm text-n-slate-11">
      {{ t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.LOADING') }}
    </p>

    <p v-else-if="previewError" class="mb-0 text-sm text-n-ruby-11">
      {{ previewError }}
    </p>

    <div
      v-else-if="preview"
      class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-slate-2 p-4"
    >
      <div
        v-for="line in preview.line_items"
        :key="line.metadata?.limit_key"
        class="flex items-start justify-between gap-3 text-sm"
      >
        <div class="min-w-0 text-n-slate-11">
          <p class="mb-0 font-medium text-n-slate-12">
            {{
              line.metadata?.limit_key === 'agents'
                ? t(
                    'GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_LINE_AGENTS',
                    {
                      qty: line.quantity,
                    }
                  )
                : t(
                    'GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_LINE_INBOXES',
                    {
                      qty: line.quantity,
                    }
                  )
            }}
          </p>
          <p class="mb-0 text-xs">
            {{
              t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_PRORATED', {
                days: line.metadata?.remaining_days,
              })
            }}
          </p>
        </div>
        <span class="shrink-0 tabular-nums font-medium text-n-slate-12">
          {{ formatMoney(line.total, preview.currency_symbol) }}
        </span>
      </div>
      <div
        class="flex items-center justify-between border-0 border-t border-solid border-n-weak pt-3 text-sm font-semibold text-n-slate-12"
      >
        <span>{{
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_SEATS_TOTAL')
        }}</span>
        <span class="tabular-nums">
          {{ formatMoney(preview.total, preview.currency_symbol) }}
          {{ preview.currency_code }}
        </span>
      </div>
    </div>
  </Dialog>
</template>
